import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'
import type { CalendarEvent, ClassSession, DaySummary } from './types'

/**
 * The classes and gym events between two days.
 *
 * Moving to the next week keeps the current one on screen until the new one
 * arrives, so paging never flashes a spinner, and a slow answer for a week
 * already left behind never overwrites the one being looked at.
 */
export function useClasses(from: string, to: string, enabled = true) {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [classes, setClasses] = useState<ClassSession[]>([])
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [canManage, setCanManage] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const latest = useRef(0)

  const reload = useCallback(async () => {
    if (!enabled) return
    const request = ++latest.current
    try {
      const token = await getAccessTokenSilently()
      const headers = { Authorization: `Bearer ${token}` }
      const range = `from=${from}&to=${to}`
      const [classesRes, eventsRes] = await Promise.all([
        fetch(`${apiUrl}/classes?${range}`, { headers }),
        fetch(`${apiUrl}/calendar-events?${range}`, { headers }),
      ])
      if (request !== latest.current) return
      if (!classesRes.ok) {
        setError(true)
        return
      }
      const data = (await classesRes.json()) as { classes: ClassSession[]; canManage: boolean }
      // The events are a courtesy: a failure there leaves the classes usable.
      const eventData = eventsRes.ok
        ? ((await eventsRes.json()) as { events: CalendarEvent[] }).events
        : []
      if (request !== latest.current) return
      setClasses(data.classes)
      setCanManage(data.canManage)
      setEvents(eventData)
      setError(false)
    } catch {
      if (request === latest.current) setError(true)
    } finally {
      if (request === latest.current) setLoading(false)
    }
  }, [apiUrl, getAccessTokenSilently, from, to, enabled])

  useEffect(() => {
    void reload()
  }, [reload])

  return { classes, events, canManage, loading, error, reload }
}

/** Classes and events counted per day, for the year overview. Writes nothing. */
export function useClassSummary(from: string, to: string, enabled = true) {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [days, setDays] = useState<Record<string, DaySummary>>({})
  const [events, setEvents] = useState<Record<string, number>>({})
  const [canManage, setCanManage] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const latest = useRef(0)

  useEffect(() => {
    if (!enabled) return
    const request = ++latest.current
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/classes/summary?from=${from}&to=${to}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (request !== latest.current) return
        if (!res.ok) {
          setError(true)
          return
        }
        const data = (await res.json()) as {
          days: Record<string, DaySummary>
          events: Record<string, number>
          canManage: boolean
        }
        if (request !== latest.current) return
        setDays(data.days)
        setEvents(data.events)
        setCanManage(data.canManage)
        setError(false)
      } catch {
        if (request === latest.current) setError(true)
      } finally {
        if (request === latest.current) setLoading(false)
      }
    })()
  }, [apiUrl, getAccessTokenSilently, from, to, enabled])

  return { days, events, canManage, loading, error }
}
